import { useCallback, useEffect, useState } from 'react';
import { get } from '../api/client';

/**
 * Fetches a URL and keeps { data, meta, loading, error, reload } in sync.
 * `deps` re-runs the request; `skip` defers it (e.g. until an event is chosen).
 */
export default function useFetch(url, params = {}, { skip = false, withMeta = false } = {}) {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);

  // Serialised so callers can pass an object literal without looping forever.
  const key = JSON.stringify(params);

  const load = useCallback(async () => {
    if (skip || !url) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await get(url, JSON.parse(key), withMeta);
      if (withMeta) { setData(res.data); setMeta(res.meta); }
      else setData(res);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [url, key, skip, withMeta]);

  useEffect(() => { load(); }, [load]);

  return { data, meta, loading, error, reload: load, setData };
}
